import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsAPI, isGoogleMapsLoaded } from '../utils/googleMaps';

interface VenueMapProps {
  address?: string;
  latitude?: number;
  longitude?: number;
  venueName?: string;
  height?: string;
  zoom?: number;
  showMarker?: boolean;
  interactive?: boolean;
  onLocationChange?: (lat: number, lng: number) => void;
}

const defaultCenter = {
  lat: 37.7749, // San Francisco
  lng: -122.4194
};

const VenueMap: React.FC<VenueMapProps> = ({
  address,
  latitude,
  longitude,
  venueName = 'Venue Location',
  height = '400px',
  zoom = 15,
  showMarker = true,
  interactive = true,
  onLocationChange
}) => {
  console.log('VenueMap: Component rendering', { latitude, longitude, address });
  
  const [mapCenter, setMapCenter] = useState(
    latitude && longitude 
      ? { lat: latitude, lng: longitude }
      : defaultCenter
  );
  const [markerPosition, setMarkerPosition] = useState(mapCenter);
  const [isMapReady, setIsMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  // Initialize Google Maps when container is ready
  useEffect(() => {
    console.log('VenueMap: useEffect triggered, mapContainer =', mapContainer);
    if (!mapContainer) {
      console.log('VenueMap: No container yet, waiting...');
      return;
    }

    const initMap = async () => {
      try {
        console.log('VenueMap: Initializing map...');
        console.log('VenueMap: mapContainer =', mapContainer);
        
        // Load Google Maps API if not already loaded
        if (!isGoogleMapsLoaded()) {
          console.log('VenueMap: Loading Google Maps API...');
          await loadGoogleMapsAPI();
          console.log('VenueMap: Google Maps API loaded');
        } else {
          console.log('VenueMap: Google Maps already loaded');
        }

        // Check if google.maps is available
        if (!window.google || !window.google.maps) {
          throw new Error('Google Maps not available after loading');
        }

        // Create map
        if (!googleMapRef.current) {
          console.log('VenueMap: Creating map instance...');
          googleMapRef.current = new window.google.maps.Map(mapContainer, {
            center: mapCenter,
            zoom: zoom,
            disableDefaultUI: !interactive,
            zoomControl: interactive,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: interactive,
          });

          // Create geocoder
          geocoderRef.current = new window.google.maps.Geocoder();

          // Add click listener if interactive
          if (interactive && onLocationChange) {
            googleMapRef.current.addListener('click', (e: google.maps.MapMouseEvent) => {
              if (e.latLng) {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                setMarkerPosition({ lat, lng });
                updateMarker({ lat, lng });
                onLocationChange(lat, lng);
              }
            });
          }

          console.log('VenueMap: Map created successfully');
          setIsMapReady(true);
        }
      } catch (err) {
        console.error('VenueMap: Failed to initialize map:', err);
        setError(err instanceof Error ? err.message : 'Failed to load Google Maps');
      }
    };

    initMap();
  }, [mapContainer]);

  // Update map center when props change
  useEffect(() => {
    if (googleMapRef.current && latitude && longitude) {
      const newCenter = { lat: latitude, lng: longitude };
      googleMapRef.current.setCenter(newCenter);
      setMapCenter(newCenter);
      setMarkerPosition(newCenter);
      updateMarker(newCenter);
    }
  }, [latitude, longitude]);

  // Geocode address if no coordinates provided
  useEffect(() => {
    if (googleMapRef.current && geocoderRef.current && address && !latitude && !longitude) {
      geocoderRef.current.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          const newCenter = { lat: location.lat(), lng: location.lng() };
          googleMapRef.current!.setCenter(newCenter);
          setMapCenter(newCenter);
          setMarkerPosition(newCenter);
          updateMarker(newCenter);
        }
      });
    }
  }, [address, latitude, longitude]);

  const updateMarker = (position: { lat: number; lng: number }) => {
    if (!googleMapRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker if showMarker is true
    if (showMarker) {
      markerRef.current = new window.google.maps.Marker({
        position,
        map: googleMapRef.current,
        title: venueName,
        animation: window.google.maps.Animation.DROP,
      });
    }
  };

  if (error) {
    return (
      <div 
        className="bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400"
        style={{ height }}
      >
        <div className="text-center">
          <p className="font-medium">Map unavailable</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: '100%', height }}>
      <div 
        ref={(el) => {
          console.log('VenueMap: Ref callback, element =', el);
          setMapContainer(el);
        }}
        className="rounded-lg"
        style={{ width: '100%', height: '100%' }}
      />
      {!isMapReady && (
        <div 
          className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400"
        >
          <div className="text-center">
            <p>Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueMap;